import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import BrandLayout from "@/components/layout/BrandLayout";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Search, Eye, CheckCircle, XCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const BrandSubmissions = () => {
  const { token } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [actionDialog, setActionDialog] = useState({ open: false, type: null, submission: null });

  const fetchSubmissions = async () => {
    try {
      const response = await axios.get(`${API}/brand/submissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmissions(response.data);
      setFilteredSubmissions(response.data);
    } catch (error) {
      console.error("Failed to fetch submissions:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubmissions();
  }, [token]);

  useEffect(() => {
    let filtered = submissions;
    
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(s => 
        s.artist_name.toLowerCase().includes(searchLower) ||
        s.venue.toLowerCase().includes(searchLower) ||
        s.city.toLowerCase().includes(searchLower)
      );
    }
    
    if (statusFilter !== "all") {
      filtered = filtered.filter(s => s.status === statusFilter);
    }
    
    setFilteredSubmissions(filtered);
  }, [search, statusFilter, submissions]);

  const handleApprove = async () => {
    if (!actionDialog.submission) return;
    
    try {
      await axios.put(
        `${API}/brand/submissions/${actionDialog.submission.id}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Submission approved!");
      fetchSubmissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve submission");
    } finally {
      setActionDialog({ open: false, type: null, submission: null });
    }
  };

  const handleReject = async () => {
    if (!actionDialog.submission) return;
    
    try {
      await axios.put(
        `${API}/brand/submissions/${actionDialog.submission.id}/reject`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Submission rejected");
      fetchSubmissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject submission");
    } finally {
      setActionDialog({ open: false, type: null, submission: null });
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending_brand_approval: "bg-[#F58220]/20 text-[#F58220]",
      approved: "bg-green-500/20 text-green-400",
    };
    const labels = {
      pending_brand_approval: "Pending Review",
      approved: "Approved",
    };
    return (
      <span className={`inline-flex items-center px-3 py-1 text-xs font-semibold uppercase tracking-wider ${styles[status] || "bg-gray-500/20 text-gray-400"}`}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <BrandLayout>
      <div className="animate-fade-in" data-testid="brand-submissions">
        <div className="mb-8">
          <h1 className="text-4xl font-heading text-white mb-2">SUBMISSIONS</h1>
          <p className="text-gray-400">Review agency-approved campaign submissions</p>
        </div>

        {/* Filters */}
        <div className="bg-gray-900 border border-gray-800 p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
              <Input
                placeholder="Search by artist, venue, or city..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-12 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48 h-12 bg-gray-800 border-gray-700 text-white" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending_brand_approval">Pending Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Submissions Table */}
        <div className="bg-gray-900 border border-gray-800 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading submissions...</div>
          ) : filteredSubmissions.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {search || statusFilter !== "all" ? "No matching submissions found" : "No submissions yet"}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-800">
                    <th className="text-left font-heading uppercase text-xs tracking-wider text-gray-400 py-4 px-4">Artist</th>
                    <th className="text-left font-heading uppercase text-xs tracking-wider text-gray-400 py-4 px-4">City</th>
                    <th className="text-left font-heading uppercase text-xs tracking-wider text-gray-400 py-4 px-4">Venue</th>
                    <th className="text-left font-heading uppercase text-xs tracking-wider text-gray-400 py-4 px-4">Date</th>
                    <th className="text-left font-heading uppercase text-xs tracking-wider text-gray-400 py-4 px-4">Status</th>
                    <th className="text-left font-heading uppercase text-xs tracking-wider text-gray-400 py-4 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubmissions.map((submission) => (
                    <tr key={submission.id} className="border-b border-gray-800 hover:bg-gray-800/50">
                      <td className="py-4 px-4">
                        <div>
                          <p className="font-medium text-white">{submission.artist_name}</p>
                          <p className="text-xs text-gray-500">{submission.artist_email}</p>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-gray-400">{submission.city}</td>
                      <td className="py-4 px-4 text-gray-400">{submission.venue}</td>
                      <td className="py-4 px-4 text-gray-400">{submission.date}</td>
                      <td className="py-4 px-4">{getStatusBadge(submission.status)}</td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <Link 
                            to={`/brand/submissions/${submission.id}`}
                            className="inline-flex items-center gap-1 text-[#F58220] hover:underline text-sm font-medium"
                          >
                            <Eye size={16} />
                            View
                          </Link>
                          {submission.status === "pending_brand_approval" && (
                            <>
                              <Button
                                size="sm"
                                className="bg-green-500 hover:bg-green-600 text-white h-8 px-3"
                                onClick={() => setActionDialog({ open: true, type: "approve", submission })}
                                data-testid={`approve-${submission.id}`}
                              >
                                <CheckCircle size={14} className="mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-[#ED5A34] text-[#ED5A34] hover:bg-[#ED5A34] hover:text-white h-8 px-3"
                                onClick={() => setActionDialog({ open: true, type: "reject", submission })}
                                data-testid={`reject-${submission.id}`}
                              >
                                <XCircle size={14} className="mr-1" />
                                Reject
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-4 text-sm text-gray-500">
          Showing {filteredSubmissions.length} of {submissions.length} submissions
        </div>

        {/* Approval/Rejection Dialog */}
        <AlertDialog open={actionDialog.open} onOpenChange={(open) => !open && setActionDialog({ open: false, type: null, submission: null })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {actionDialog.type === "approve" ? "Approve Submission" : "Reject Submission"}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {actionDialog.type === "approve" 
                  ? `Are you sure you want to approve the submission from ${actionDialog.submission?.artist_name}? The artist will be notified.`
                  : `Are you sure you want to reject the submission from ${actionDialog.submission?.artist_name}? The artist will be notified.`
                }
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={actionDialog.type === "approve" ? handleApprove : handleReject}
                className={actionDialog.type === "approve" ? "bg-green-500 hover:bg-green-600" : "bg-[#ED5A34] hover:bg-[#d44a28]"}
              >
                {actionDialog.type === "approve" ? "Approve" : "Reject"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </BrandLayout>
  );
};

export default BrandSubmissions;
